import React from "react";
import { Alert, Button, Collapse } from "@mui/material";
import { useDDSConnection, reconnectDDS } from "../../dds/useDDSConnection";

/**
 * "Reconnecting…" / "Offline" / "Not updating" banner.
 *
 * Drive's DDS subscriptions pass no onError (src/dds/useDDSSubscription.js), so
 * a DDS outage errors into the console and the UI silently freezes on stale
 * data. For a TXLive operator that is a scoreline that stops moving with no
 * indication whether the feed died or the app did.
 *
 * `stale` — connected, but nothing received recently — is the state that
 * matters, and the one a plain connected/disconnected indicator cannot express.
 */
export function DDSStatusBanner() {
  const conn = useDDSConnection();
  if (!conn) return null;

  const { status, stale, failureCount } = conn;
  const offline = status === "offline";
  const reconnecting = status === "reconnecting" || status === "connecting";
  if (!(offline || reconnecting || stale)) return null;

  return (
    <Collapse in>
      <Alert
        severity={offline ? "error" : stale ? "warning" : "info"}
        sx={{ borderRadius: 0 }}
        action={
          offline || stale ? (
            <Button color="inherit" size="small" onClick={reconnectDDS}>
              Retry now
            </Button>
          ) : null
        }
      >
        {offline
          ? "Live data disconnected. Retrying periodically."
          : reconnecting
            ? `Reconnecting to live data${failureCount > 1 ? ` (attempt ${failureCount})` : ""}…`
            : "Connected, but no live updates recently — data on screen may be stale."}
      </Alert>
    </Collapse>
  );
}

export default DDSStatusBanner;
